import React from 'react'
import EditText from 'blocks.simple/editText/editText'
import Icon from 'blocks.simple/icon/icon'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { color } from 'theme'
import { catalogTabs } from '../templateEditor.data'
import { useTemplateEditorContext } from '../hooks/templateEditorContext'
import { CatalogFileItem } from '../templateEditor.types'
import { TemplateEditorLeftPane } from './templateEditor__leftPane'
import styles from './templateEditor__leftPanels.jcss'

type CatalogBreadcrumb = {
    id: number | null
    name: string
}

const ROOT_BREADCRUMB_NAME = 'root'

const getCatalogBreadcrumbLabel = (name: string) =>
    name === ROOT_BREADCRUMB_NAME ? translate('templateEditorCatalogRoot') : name

type CatalogBreadcrumbsProps = {
    breadcrumbs: CatalogBreadcrumb[]
    onNavigate: (index: number) => void
}

const CatalogBreadcrumbs = ({ breadcrumbs, onNavigate }: CatalogBreadcrumbsProps) => {
    const lastIndex = breadcrumbs.length - 1

    return (
        <nav className={styles.catalogBreadcrumbs} aria-label={translate('templateEditorCatalogImages')}>
            {breadcrumbs.map((crumb, index) => {
                const isLast = index === lastIndex
                const label = getCatalogBreadcrumbLabel(crumb.name)
                const key = `${crumb.id ?? ROOT_BREADCRUMB_NAME}-${index}`

                return (
                    <span key={key} className={styles.catalogBreadcrumbItem}>
                        {index > 0 && <span className={styles.catalogBreadcrumbSep}>/</span>}
                        {isLast ? (
                            <span className={styles.catalogBreadcrumbCurrent}>{label}</span>
                        ) : (
                            <button
                                type="button"
                                className={styles.catalogBreadcrumbLink}
                                onClick={() => onNavigate(index)}
                            >
                                {label}
                            </button>
                        )}
                    </span>
                )
            })}
        </nav>
    )
}

type Props = {
    collapsed: boolean
    onToggle: () => void
}

export const TemplateEditorCatalogPane = ({ collapsed, onToggle }: Props) => {
    const {
        catalogTab,
        setCatalogTab,
        catalog,
        enterPlaceMode,
        setCanvasBackgroundMedia,
        placeMode,
        imageReplaceTargetId,
        applyImageReplaceFromCatalog,
    } = useTemplateEditorContext()

    const isReplaceMode = Boolean(imageReplaceTargetId)

    const handleCatalogItemClick = (item: CatalogFileItem) => {
        if (item.isDirectory) {
            catalog.openFolder(item)
            return
        }

        if (isReplaceMode) {
            if (item.fileType !== 'image') {
                return
            }

            const payload = catalog.catalogPayloadFromItem(item)
            if (payload) {
                applyImageReplaceFromCatalog(payload)
            }
            return
        }

        if (catalogTab === 'backgrounds') {
            const bg = catalog.backgroundFromItem(item)
            if (bg) {
                setCanvasBackgroundMedia(bg)
            }
            return
        }

        const payload = catalog.catalogPayloadFromItem(item)
        if (payload) {
            enterPlaceMode(item.fileType === 'video' ? 'video' : 'image', payload)
        }
    }

    const isCatalogItemActive = (item: CatalogFileItem): boolean =>
        Boolean(
            !item.isDirectory &&
                placeMode &&
                (placeMode.type === 'image' || placeMode.type === 'video') &&
                placeMode.opts.sourceId === item.id
        )

    return (
        <TemplateEditorLeftPane
            paneId="catalog"
            title={translate('templateEditorCatalogImages')}
            collapsed={collapsed}
            onToggle={onToggle}
        >
            <div className={styles.catalogToolbar}>
                <div className={styles.panelTabs}>
                    {catalogTabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={cn(styles.panelTab, catalogTab === tab.id && styles.panelTabActive)}
                            onClick={() => setCatalogTab(tab.id)}
                        >
                            {translate(tab.labelKey)}
                        </button>
                    ))}
                </div>
                <div className={styles.catalogSearchWrap}>
                    <Icon type="search" size={14} />
                    <EditText
                        className={styles.catalogSearch}
                        placeholder={translate('search')}
                        value={catalog.catalogSearch}
                        onChange={catalog.setCatalogSearch}
                        rounded="none"
                    />
                </div>
                <CatalogBreadcrumbs breadcrumbs={catalog.breadcrumbs} onNavigate={catalog.goToBreadcrumb} />
            </div>
            <div className={cn(styles.catalogGrid, isReplaceMode && styles.catalogGridReplaceMode)}>
                {catalog.loading && (
                    <div className={styles.catalogEmpty}>
                        <span className={styles.catalogSpinner} aria-hidden="true" />
                        {translate('templateEditorCatalogLoading')}
                    </div>
                )}
                {!catalog.loading && catalog.items.length === 0 && (
                    <div className={styles.catalogEmpty}>{translate('templateEditorCatalogEmpty')}</div>
                )}
                {!catalog.loading &&
                    catalog.items.map((item) => {
                        const thumbSrc = item.isDirectory ? undefined : catalog.getThumbSrc(item)
                        const replaceDisabled = isReplaceMode && !item.isDirectory && item.fileType !== 'image'

                        return (
                            <button
                                key={`${item.isDirectory ? 'folder' : 'file'}-${item.id}`}
                                type="button"
                                className={cn(
                                    styles.catalogItem,
                                    isCatalogItemActive(item) && styles.catalogItemActive,
                                    replaceDisabled && styles.catalogItemDisabled
                                )}
                                title={item.name}
                                draggable={!item.isDirectory && !isReplaceMode}
                                disabled={replaceDisabled}
                                onDragStart={(event) => {
                                    if (item.isDirectory || isReplaceMode) {
                                        return
                                    }
                                    event.dataTransfer.setData('application/json', JSON.stringify(item))
                                    event.dataTransfer.effectAllowed = 'copy'
                                }}
                                onClick={() => handleCatalogItemClick(item)}
                            >
                                {item.isDirectory ? (
                                    <div className={cn(styles.catalogThumb, styles.catalogThumbFolder)}>
                                        <Icon type="folder" size={36} color={color.iconDarkMedium} />
                                    </div>
                                ) : thumbSrc ? (
                                    <img className={styles.catalogThumbImage} src={thumbSrc} alt="" />
                                ) : (
                                    <div className={cn(styles.catalogThumb, styles.catalogThumbFallback)} />
                                )}
                                {isCatalogItemActive(item) && (
                                    <span className={styles.catalogItemCheck} aria-hidden="true">
                                        <Icon type="checked" size={11} color="#fff" />
                                    </span>
                                )}
                                <span className={styles.catalogItemName}>{item.name}</span>
                            </button>
                        )
                    })}
            </div>
        </TemplateEditorLeftPane>
    )
}
